<template>
    <div>
        <div id="jw-video"></div>
        <CourseTestNotificationForVideo
            v-if="showCourseTestNotification && videoFinished"
            v-on:hideCourseTestNotification="hideCourseTestNotification"
            :isLastCollectionVideo="isLastCollectionVideo"
            :isCollectionCoursePage="isCollectionCoursePage"
        />
        <VideoRedirectionCountdown v-else-if="videoFinished && displayVideoRedirectionBox"
            :nextVideoTitle="this.videoRedirectionNextSectionTitle"
            :countdown="this.videoRedirectionCountdown"
            v-on:countDownComplete="countDownComplete"
        />
    </div>
</template>

<script>
    import rangeHandler from 'helpers/rangeHandler'
    import { mapGetters } from 'vuex'
    import microLearningHelper from 'components/shared/mixins/microLearningHelper'
    import VideoRedirectionCountdown from 'components/coursepage/coursevideo/VideoRedirectionCountdown'
    import videoRedirections from 'components/shared/mixins/videoRedirections'
    import CourseTestNotificationForVideo from './CourseTestNotificationForVideo.vue'
    import courseTestHelper from 'components/shared/mixins/courseTestHelper'
    import config from 'services/config'
    import moment from 'moment'
    import AutoPlayHelper from 'components/coursepage/coursevideo/helper/AutoPlayHelper'

    export default {
        name: 'JWPlayerVideo',
        mixins: [ microLearningHelper, videoRedirections, courseTestHelper, AutoPlayHelper ],
        components: {
            VideoRedirectionCountdown,
            CourseTestNotificationForVideo
        },
        data () {
            return {
                player: null,
                currentlyWatchedSequences: [],
                duration: 99999999999,
                currentPosition: 0,
                currentSeqtionStart: 0,
                progressInterval: null,
                videoInterval: null,
                saveVideoInterval: null,
                demoInterval: null,
                resetAfterSeeked: false,
                durationCheckoutInterval: null,
                demoWatcher: null,
                isDemoStopped: true,
                demoStartTime: null,
                loadInterval: null,
                showVideoRedirectionCountdown: false,
                videoFinished: false,
                showCourseTestNotification: false,
                playerType: 'JWPlayer'
            }
        },
        computed: {
            ...mapGetters({
                moveTarget: 'VideoPlayer/moveTarget',
                showOnlyDemo: 'VideoPlayer/showOnlyDemo',
                demoWatchTimeLimit: 'VideoPlayer/demoWatchTimeLimit',
                videoProps: 'VideoPlayer/videoProps',
                videoStartPosition: 'VideoPlayer/position',
                totalWatchedAmount: 'VideoPlayer/totalWatchedAmount',
                course: 'Courses/getSingle',
                liveWatchedTimeById: 'getLiveWatchedTime',
                liveWatchedTime: 'VideoPlayer/getLiveWatchedTime',
                microLearningSections: 'MicroLearning/getMicroLearningSections',
                microLearningSection: 'MicroLearning/getMicroLearningSection',
                shouldAutoPlay: 'VideoPlayer/shouldAutoPlay',
                isRatingModalVisible: 'VideoPlayer/getIsRatingModalVisible'
            }),
            getProperStructureOfLink () {
                if (this.videoProps.indexOf('-') > 0) {
                    const id = this.videoProps.replace('jwplayer:', '')
                    const parts = id.split('-')
                    return parts[0]
                } else {
                    return this.videoProps.replace('jwplayer:', '')
                }
            },
            isLive () {
                return this.duration < 0 || Number.isNaN(this.duration)
            },
            getVideoLogIntervalTime () {
                const now = moment().unix()
                return now > config.customVideoLogIntervalStartTime &&
                    now < config.customVideoLogIntervalEndTime ? config.customVideoLogIntervalMilliseconds : 30000
            }
        },
        watch: {
            moveTarget: {
                handler: 'moveVideoToPoint'
            },
            isRatingModalVisible: {
                handler: 'pauseVideoIfRatingModalIsVisible'
            }
        },
        methods: {
            moveVideoToPoint (point) {
                if (this.player) {
                    this.player.seek(point)
                }
            },
            pauseVideoIfRatingModalIsVisible (isRatingModalVisible) {
                isRatingModalVisible ? this.player.pause() : this.player.play()
            },
            onScriptLoaded () {
                this.player = jwplayer('jw-video')
                this.player.setup({
                    playlist: `https://cdn.jwplayer.com/v2/media/${this.getProperStructureOfLink}`
                })
                this.player.on('ready', this.onPlayerReady)
                this.player.on('seek', this.onPlayerSeek)
                this.player.on('play', this.onPlayerPlay)
                this.player.on('complete', this.onPlayerCompleted)
                this.player.on('fullscreen', this.onPlayerFullScreen)
                this.player.on('pause', this.onPlayerPause)
            },
            onPlayerFullScreen () {
                if (window.HubSpotConversations && window.HubSpotConversations.widget) {
                    const isFullScreenMode = this.player.getFullscreen()
                    isFullScreenMode ? window.HubSpotConversations.widget.remove() : window.HubSpotConversations.widget.load()
                }
            },
            onPlayerReady () {
                if (!this.showOnlyDemo && !this.shouldAutoPlayVideo) {
                    this.moveVideoToPoint(this.videoStartPosition)
                    this.currentSeqtionStart = this.videoStartPosition
                    this.currentPosition = this.videoStartPosition
                }
                this.getDuration()
                this.player.stop()

                this.isDemoStopped = true
                this.demoStartTime = null

                if (this.isLive && !this.showOnlyDemo) {
                    this.$store.dispatch('VideoPlayer/updateLiveWatchedTime', this.liveWatchedTimeById(this.course.id))
                }

                if (!this.showOnlyDemo) {
                    this.videoInterval = setInterval(() => this.$emit('persistPlayedTime'), 1000)
                    this.saveVideoInterval = setInterval(() => this.$emit('saveVideoLog'), this.getVideoLogIntervalTime)
                    this.progressInterval = setInterval(this.supportProgress, 3000)
                }
                if (this.shouldAutoPlayVideo) {
                    setTimeout(() => this.player.play(), 250)
                }
            },
            onPlayerSeek (data) {
                if (this.showOnlyDemo) {
                    if (data.offset !== 0) {
                        this.$nextTick(() => {
                            this.stopDemo()
                        })
                    }
                }

                if (!this.showOnlyDemo) {
                    const lastStart = this.currentSeqtionStart
                    const now = this.player.getCurrentTime()
                    if (lastStart < now) {
                        this.currentlyWatchedSequences = rangeHandler.unifyRanges(this.currentlyWatchedSequences, [[lastStart, now]])
                    }
                    this.currentSeqtionStart = data.offset
                    this.saveVideoProgress(data.offset)
                }
            },
            async saveVideoProgress (position) {
                this.getDuration()
                await this.$emit('progress', {
                    currentPosition: position,
                    currentSessionSequences: this.currentlyWatchedSequences,
                    videoDuration: this.duration
                })
                this.$emit('persistPlayedTime')
                this.$emit('saveVideoLog')
            },
            onPlayerPlay () {
                if (!this.duration && this.showOnlyDemo) {
                    this.getDuration()
                    return this.player.pause()
                }

                if (this.showOnlyDemo && this.isDemoStopped) {
                    this.startDemo()
                }
            },
            onPlayerPause () {
                let currentTime = this.player.getCurrentTime()
                this.saveVideoProgress(currentTime)
                this.$emit('saveVideoLog')
            },
            startDemo () {
                if (this.duration) {
                    this.demoStartTime = +new Date()
                    this.isDemoStopped = false
                    this.demoInterval = setInterval(() => {
                        if (+new Date() - this.demoStartTime > (this.duration * 50)) {
                            this.stopDemo()
                        }
                    }, 30)
                }
            },
            stopDemo () {
                this.player.seek(0)
                clearInterval(this.demoInterval)
                setTimeout(() => {
                    this.player.pause()
                    this.isDemoStopped = true
                    this.$emit('demoFinished')
                }, 50)
            },
            getDuration () {
                this.duration = parseInt(this.player.getDuration())
            },
            async supportProgress () {
                const lastStart = this.currentSeqtionStart
                this.currentPosition = this.player.getCurrentTime()
                if (this.isLive && this.player.getState() === 'playing') {
                    await this.$store.dispatch('progressLive', this.course.id)
                    this.$store.dispatch('VideoPlayer/updateLiveWatchedTime', this.liveWatchedTimeById(this.course.id))
                }
                this.getDuration()
                if (this.currentPosition > lastStart) {
                    this.currentlyWatchedSequences = rangeHandler.unifyRanges(this.currentlyWatchedSequences, [[lastStart, this.currentPosition]])
                }
                this.$emit('progress', {
                    currentPosition: this.currentPosition,
                    currentSessionSequences: this.currentlyWatchedSequences,
                    videoDuration: this.duration,
                })
            },
            hideCourseTestNotification () {
                this.showCourseTestNotification = false
                this.redirectToNext()
            }
        },
        created () {
            if (this.videoProps.indexOf('jwplayer:') === -1) {
                return this.$emit('invalid-video-url')
            }

            this.loadInterval = setInterval(() => {
                if (window.jwplayer) {
                    this.onScriptLoaded()
                    clearInterval(this.loadInterval)
                }
            }, 50)
        },
        beforeDestroy () {
            clearInterval(this.demoInterval)
            clearInterval(this.videoInterval)
            clearInterval(this.saveVideoInterval)
            clearInterval(this.progressInterval)
            clearInterval(this.loadInterval)
            clearInterval(this.nextVideoInterval)
            if (this.player) {
                document.body.style.overflowY = null
                this.player.remove()
            }
        }
    }
</script>

<style scoped>

.fade-enter-active,
.fade-leave-active {
  transition: opacity 3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

</style>
